import React from 'react'
import {
  Cell as AriaCell,
  Column as AriaColumn,
  Row as AriaRow,
  Table as AriaTable,
  TableBody as AriaTableBody,
  TableHeader as AriaTableHeader,
  Button,
  Collection,
  ColumnProps,
  RowProps,
  TableBodyProps,
  TableHeaderProps,
  TableProps,
  useTableOptions,
} from 'react-aria-components'
import { Checkbox } from '../Checkbox'

import './Table.css'

export function Table(props: TableProps) {
  return <AriaTable {...props} />
}

export function Column(props: Omit<ColumnProps, 'children'> & { children?: React.ReactNode }) {
  return (
    <AriaColumn {...props} style={{ ...props.style, width: props.width ?? undefined }}>
      {({ allowsSorting, sortDirection }) => (
        <>
          {props.children}
          {allowsSorting && (
            <span aria-hidden="true" className="sort-indicator">
              {sortDirection === 'ascending' ? '▲' : '▼'}
            </span>
          )}
        </>
      )}
    </AriaColumn>
  )
}

export function TableHeader<T extends object>({ columns, children }: TableHeaderProps<T>) {
  const { selectionBehavior, selectionMode, allowsDragging } = useTableOptions()

  return (
    <AriaTableHeader>
      {/* Add extra columns for drag and drop and selection. */}
      {allowsDragging && <AriaColumn />}
      {selectionBehavior === 'toggle' && (
        <AriaColumn>{selectionMode === 'multiple' && <Checkbox slot="selection" />}</AriaColumn>
      )}
      <Collection items={columns}>{children}</Collection>
    </AriaTableHeader>
  )
}

export function TableBody<T extends object>(props: TableBodyProps<T>) {
  return <AriaTableBody {...props} />
}

export function Row<T extends object>({ id, columns, children, ...otherProps }: RowProps<T>) {
  const { selectionBehavior, allowsDragging } = useTableOptions()

  return (
    <AriaRow id={id} {...otherProps}>
      {allowsDragging && (
        <AriaCell>
          <Button slot="drag">≡</Button>
        </AriaCell>
      )}
      {selectionBehavior === 'toggle' && (
        <AriaCell>
          <Checkbox slot="selection" />
        </AriaCell>
      )}
      <Collection items={columns}>{children}</Collection>
    </AriaRow>
  )
}

export const Cell = AriaCell
